import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import { queryClient } from './lib/queryClient';
import { useAuth } from './hooks/useAuth';
import { useAnalytics } from './hooks/useAnalytics';
import { TabBar } from './components/TabBar';
import { DesktopSidebar } from './components/DesktopSidebar/DesktopSidebar';
import { AnnouncementModal } from './components/AnnouncementModal/AnnouncementModal';
import { CookieBanner } from './components/CookieBanner/CookieBanner';
import { useIsDesktop } from './hooks/useMediaQuery';
import { Login } from './pages/login/Login';
import { getTelegramWebApp, initWebApp, initBrowserTheme } from './lib/telegram';
import { ScrollToTop } from './components/ScrollToTop';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import './App.css';

const YandexCallback = lazy(() => import('./pages/yandexCallback/YandexCallback').then((m) => ({ default: m.YandexCallback })));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const Expenses = lazy(() => import('./pages/expenses/Expenses').then((m) => ({ default: m.Expenses })));
const AddExpense = lazy(() => import('./pages/addExpense/AddExpense').then((m) => ({ default: m.AddExpense })));
const NewProject = lazy(() => import('./pages/newProject/NewProject').then((m) => ({ default: m.NewProject })));
const EditProject = lazy(() => import('./pages/editProject/EditProject').then((m) => ({ default: m.EditProject })));
const BudgetCalculator = lazy(() => import('./pages/budgetCalculator/BudgetCalculator').then((m) => ({ default: m.BudgetCalculator })));
const Stages = lazy(() => import('./pages/stages/Stages').then((m) => ({ default: m.Stages })));
const StageDetail = lazy(() => import('./pages/stageDetail/StageDetail').then((m) => ({ default: m.StageDetail })));
const AddStage = lazy(() => import('./pages/addStage/AddStage').then((m) => ({ default: m.AddStage })));
const KanbanBoard = lazy(() => import('./pages/kanbanBoard/KanbanBoard').then((m) => ({ default: m.KanbanBoard })));
const Materials = lazy(() => import('./pages/materials/Materials').then((m) => ({ default: m.Materials })));
const MaterialDetail = lazy(() => import('./pages/materialDetail/MaterialDetail').then((m) => ({ default: m.MaterialDetail })));
const AddMaterial = lazy(() => import('./pages/addMaterial/AddMaterial').then((m) => ({ default: m.AddMaterial })));
const Support = lazy(() => import('./pages/support/Support').then((m) => ({ default: m.Support })));
const Profile = lazy(() => import('./pages/profile/Profile').then((m) => ({ default: m.Profile })));
const AcceptInvitation = lazy(() => import('./pages/acceptInvitation/AcceptInvitation').then((m) => ({ default: m.AcceptInvitation })));
const ProjectMembers = lazy(() => import('./pages/projectMembers/ProjectMembers').then((m) => ({ default: m.ProjectMembers })));
const ProjectFiles = lazy(() => import('./pages/projectFiles/ProjectFiles').then((m) => ({ default: m.ProjectFiles })));
const PlansEntry = lazy(() => import('./pages/plansEntry/PlansEntry').then((m) => ({ default: m.PlansEntry })));
const FloorPlans = lazy(() => import('./pages/floorPlans/FloorPlans').then((m) => ({ default: m.FloorPlans })));
const FloorPlanEditor = lazy(() => import('./pages/floorPlanEditor/FloorPlanEditor').then((m) => ({ default: m.FloorPlanEditor })));
const Reports = lazy(() => import('./pages/reports/Reports').then((m) => ({ default: m.Reports })));

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="app-loading">
      <div className="spinner"></div>
      <p>{t('common.loading')}</p>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  useAnalytics();
  const isExpenseForm = /^\/projects\/[^/]+\/expenses\/(?:new|[^/]+\/(?:edit|copy))\/?$/.test(
    location.pathname,
  );
  // Редактор планировки полноэкранный: таб-бар перекрыл бы его панель инструментов.
  const isPlanEditor = /^\/projects\/[^/]+\/plans\/[^/]+\/?$/.test(location.pathname);
  const showTabBar = !isDesktop && location.pathname !== '/projects/new' && !isExpenseForm && !isPlanEditor;

  // Обработка Яндex OAuth callback
  if (location.pathname === '/auth/yandex/callback') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <YandexCallback />
      </Suspense>
    );
  }

  useEffect(() => {
    if (getTelegramWebApp()) {
      initWebApp();
    } else {
      initBrowserTheme();
    }
  }, []);

  const { isLoading, error, user, needsTelegramOAuth, authenticateWithOAuth } = useAuth();

  // Логика авторизации:
  // 1. Если внутри Telegram WebApp -> useAuth использует initData автоматически
  // 2. Если в вебе и есть активная сессия (токен) -> useAuth логинит автоматически
  // 3. Если в вебе и нет сессии -> показываем страницу Login с выбором провайдера
  if (needsTelegramOAuth) {
    return (
      <Login
        onTelegramAuth={authenticateWithOAuth}
        error={error}
        loading={isLoading}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>{t('errors.authTitle')}</h2>
        <p>{error}</p>
        {import.meta.env.DEV && (
          <div className="dev-mode">
            <p>{t('errors.devMode')}</p>
            <p>{t('errors.devModeHint')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <div className={`app${isDesktop ? ' app-desktop' : ''}`}>
      {isDesktop && <DesktopSidebar user={user} />}
      <main className="app-content">
        {/* Падение страницы не роняет оболочку: таб-бар остаётся, переход сбрасывает ошибку. */}
        <AppErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/projects/new" element={<NewProject />} />
            <Route path="/projects/:projectId/edit" element={<EditProject />} />
            <Route path="/projects/:projectId/budget" element={<BudgetCalculator />} />
            <Route path="/projects/:projectId/expenses" element={<Expenses />} />
            <Route path="/projects/:projectId/expenses/new" element={<AddExpense />} />
            <Route path="/projects/:projectId/expenses/:expenseId/copy" element={<AddExpense />} />
            <Route path="/projects/:projectId/expenses/:expenseId/edit" element={<AddExpense />} />
            <Route path="/projects/:projectId/stages" element={<Stages />} />
            <Route path="/projects/:projectId/stages/new" element={<AddStage />} />
            <Route path="/projects/:projectId/stages/:stageId" element={<StageDetail />} />
            <Route path="/projects/:projectId/kanban" element={<KanbanBoard />} />
            <Route path="/projects/:projectId/materials" element={<Materials />} />
            <Route path="/projects/:projectId/materials/new" element={<AddMaterial />} />
            <Route path="/projects/:projectId/materials/:needId" element={<MaterialDetail />} />
            <Route path="/projects/:projectId/members" element={<ProjectMembers />} />
            <Route path="/projects/:projectId/files" element={<ProjectFiles />} />
            <Route path="/plans" element={<PlansEntry />} />
            <Route path="/materials" element={<PlansEntry section="materials" />} />
            <Route path="/projects/:projectId/plans" element={<FloorPlans />} />
            <Route path="/projects/:projectId/plans/:planId" element={<FloorPlanEditor />} />
            <Route path="/invite/:token" element={<AcceptInvitation />} />
            <Route path="/support" element={<Support />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </AppErrorBoundary>
      </main>
    </div>
    {showTabBar && <TabBar user={user} />}
    {/* В редакторе планировки плашка закрыла бы панель инструментов. */}
    {!isPlanEditor && <CookieBanner aboveTabBar={showTabBar} />}
    {/* Поверх всего: рисуется, только если в админке включён анонс. */}
    <AnnouncementModal />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
